import { ReactNode, useState } from "react";
import { useAuth } from "react-oidc-context";
import styled from "styled-components";

import { faCircleCheck, faXmark } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { colors } from "@ntemarked/mittne-components";
import Button, { type ButtonColors } from "@ntemarked/mittne-components/dist/Button/Button";

import Modal from "components/molecules/Modal";
import TextCopyButton from "components/molecules/TextCopyButton";
import Portal from "components/organisms/Portal";
import useAccess from "hooks/useAccess";
import { useStrombestillingApiMutation } from "hooks/useStrombestillingApi";
import { InnkjoepsbestillingText } from "pages/Sikring/components/InnkjoepsbestillingText";
import { MonthlyVolumeList } from "pages/Sikring/components/MonthlyVolumeList";
import { StatusBadge } from "pages/Sikring/components/StatusBadge";
import {
	FLYT_LABEL,
	STATUS_DESCRIPTION,
	STATUS_LABEL,
	isDefined,
	isDeletable,
	isDeleted,
	fmtDate,
	fmtDateTime,
	formatNumber,
	fmtPrice,
	fmtPriceWithUnit,
	monthLabel,
	scopeFull,
	PRICE_UNIT,
	priceOf,
	totalVolumeKwh,
	sortMonthlyVolumes,
} from "pages/Sikring/sikringUtils";
import { Kvalitetssikring, Sikring, SikringFlyt, Sikringsordre, Sikringsordrestatus } from "pages/Sikring/types";

interface SikringsordreDrawerProps {
	order: Sikringsordre;
	onClose: () => void;
}

export function SikringsordreDrawer({ order, onClose }: Readonly<SikringsordreDrawerProps>) {
	const { sikring } = order;
	const access = useAccess();
	const canDelete = access.includes("admin-order") && !isDeleted(order.status);
	// Samme rolle som backend krever for å kvalitetssikre.
	const canQualityCheck = access.includes("manage-sikring");
	// flyt er null for eldre sikringer, som alle gikk via kontrakt.
	const hasContract = sikring.flyt !== SikringFlyt.KunSikring;
	const volume = totalVolumeKwh(order);
	const monthlyVolumes = sortMonthlyVolumes(order.maanedsvolumer);

	const fields: { name: string; value: ReactNode }[] = [
		{ name: "Org.nr.", value: order.kunde?.organisasjonsnummer || "–" },
		{ name: "Kontaktperson", value: order.kunde?.kontaktperson || "–" },
		{ name: "Omfang", value: scopeFull(sikring) },
		...(order.maalepunkter?.length
			? [{ name: order.maalepunkter.length === 1 ? "Målepunkt-ID" : `Målepunkt (${order.maalepunkter.length})`, value: order.maalepunkter.join(", ") }]
			: []),
		{ name: "Flyt", value: sikring.flyt ? FLYT_LABEL[sikring.flyt] : "–" },
		{ name: "Periode", value: `${fmtDate(sikring.start)} – ${fmtDate(sikring.slutt)} (begge inklusive)` },
		{ name: "Volum", value: `${formatNumber(volume)} kWh` },
		{ name: "Kundepris (eks. mva)", value: fmtPriceWithUnit(priceOf(sikring)) },
		...(isDefined(sikring.innpris) || isDefined(sikring.fortjeneste)
			? [{ name: "Kjøpspris / margin (eks. mva)", value: `${fmtPrice(sikring.innpris)} + ${fmtPrice(sikring.fortjeneste)} ${PRICE_UNIT}` }]
			: []),
		{ name: "Innkjøpsbestilling", value: <InnkjoepsbestillingText skipEnergiMail={sikring.skipEnergiMail} created /> },
		{ name: "Opprettet", value: fmtDateTime(order.opprettet) },
		{ name: "Registrert av", value: order.opprettetAv || "–" },
		{ name: "Sist oppdatert", value: fmtDateTime(order.sistOppdatert) },
	];

	return (
		<Portal>
			<Backdrop onClick={onClose} />
			<Panel>
				<TopRow>
					<h2 style={{ margin: 0 }}>{order.kunde?.navn || "Sikringsordre"}</h2>
					<CloseLink
						href="#"
						onClick={(e) => {
							e.preventDefault();
							onClose();
						}}
					>
						<FontAwesomeIcon icon={faXmark} />
					</CloseLink>
				</TopRow>
				<div style={{ margin: "6px 0 6px" }}>
					<StatusBadge status={order.status} />
				</div>
				<StatusExplanation>{STATUS_DESCRIPTION[order.status]}</StatusExplanation>

				{order.status === Sikringsordrestatus.Failed && order.feilaarsak && (
					<FailureBox>
						<FailureTitle>Feilårsak</FailureTitle>
						{order.feilaarsak}
					</FailureBox>
				)}

				<FieldGrid>
					{fields.map((f) => (
						<Field key={f.name} {...f} />
					))}
				</FieldGrid>

				<QualityCheckSection
					orderId={order.id}
					status={order.status}
					opprettetAv={order.opprettetAv}
					kvalitetssikring={order.kvalitetssikring}
					canEdit={canQualityCheck}
					sikring={sikring}
					volumeKwh={volume}
				/>

				<Section>
					<SectionTitle>Månedsvolum ({monthlyVolumes.length})</SectionTitle>
					{monthlyVolumes.length === 0 ? (
						<EmptyText>Ingen månedsvolum er sendt til UtilityCloud ennå.</EmptyText>
					) : (
						<MonthlyVolumeList volumes={monthlyVolumes} />
					)}
				</Section>

				<Section>
					<details>
						<TechnicalSummary>Tekniske detaljer</TechnicalSummary>
						<TechnicalList>
							<TechnicalRow>
								<span>Ordre-ID</span>
								<Guid title={order.ordreId}>{order.ordreId}</Guid>
								<TextCopyButton text={order.ordreId} title="Kopier ordre-ID" color={colors.blue} />
							</TechnicalRow>
							{order.produktId && (
								<TechnicalRow>
									<span>Produkt-ID (UtilityCloud)</span>
									<Guid title={order.produktId}>{order.produktId}</Guid>
									<TextCopyButton text={order.produktId} title="Kopier produkt-ID" color={colors.blue} />
								</TechnicalRow>
							)}
							{monthlyVolumes.map((m) => (
								<TechnicalRow key={m.maaned}>
									<span>Korrelasjons-ID {monthLabel(m.maaned)}</span>
									<Guid title={m.correlationGuid}>{m.correlationGuid}</Guid>
									<TextCopyButton text={m.correlationGuid} title="Kopier korrelasjons-ID" color={colors.blue} />
								</TechnicalRow>
							))}
						</TechnicalList>
					</details>
				</Section>

				{canDelete && <DeleteSection orderId={order.id} deletable={isDeletable(order.status)} hasContract={hasContract} onDeleted={onClose} />}
			</Panel>
		</Portal>
	);
}

const DANGER: ButtonColors = { background: colors.coral800, backgroundPressed: colors.coral, text: colors.white, border: colors.coral800 };
const DANGER_SECONDARY: ButtonColors = { background: colors.white, backgroundPressed: colors.coral100, text: colors.coral800, border: colors.coral800 };

function deleteErrorMessage(status: number): string {
	switch (status) {
		case 404:
			return "Fant ikke sikringsordren. Den kan allerede være slettet.";
		case 409:
			return "Sikringsordren er ikke ferdig behandlet, og kan ikke slettes ennå.";
		default:
			return "Klarte ikke å slette sikringsordren. Prøv igjen senere.";
	}
}

interface DeleteSectionProps {
	orderId: string;
	/** Backend sletter bare ferdig behandlede sikringsordrer (Fullført eller Feilet). */
	deletable: boolean;
	hasContract: boolean;
	onDeleted: () => void;
}

function DeleteSection({ orderId, deletable, hasContract, onDeleted }: Readonly<DeleteSectionProps>) {
	const [confirming, setConfirming] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	const deleteMutation = useStrombestillingApiMutation(`/sikringsordre/${orderId}`, ["sikringsordre"], "DELETE", true);

	function closeConfirm() {
		if (deleteMutation.isPending) return;
		setConfirming(false);
		setErrorMessage(null);
	}

	async function deleteOrder() {
		setErrorMessage(null);
		try {
			const response = await deleteMutation.mutateAsync(undefined);
			if (!response.ok) {
				setErrorMessage(deleteErrorMessage(response.status));
				return;
			}
			onDeleted();
		} catch {
			setErrorMessage(deleteErrorMessage(0));
		}
	}

	return (
		<Section>
			<SectionTitle>Slett sikringsordre</SectionTitle>
			{!deletable ? (
				<EmptyText>Sikringsordren kan slettes når den er ferdig behandlet (Fullført eller Feilet).</EmptyText>
			) : (
				<Button type="button" secondary colors={DANGER_SECONDARY} label="Slett sikringsordre" onClick={() => setConfirming(true)} />
			)}
			{confirming && (
				<Portal>
					<Modal onClose={closeConfirm}>
						<ModalBody role="dialog" aria-modal="true" aria-labelledby="slett-sikringsordre-tittel">
							<h2 id="slett-sikringsordre-tittel" style={{ margin: "0 0 12px" }}>
								Slett sikringsordre
							</h2>
							<p style={{ margin: 0 }}>
								Er du sikker? Sikringer vil bli slettet i UtilityCloud.
								{hasContract && " Hovedprodukt og strømkontrakt vil ikke påvirkes."}
							</p>
							{errorMessage && <ErrorText>{errorMessage}</ErrorText>}
							<ButtonRow style={{ marginTop: "24px" }}>
								<Button
									type="button"
									label="Slett"
									colors={DANGER}
									loading={deleteMutation.isPending}
									disabled={deleteMutation.isPending}
									onClick={deleteOrder}
								/>
								<Button type="button" secondary label="Avbryt" disabled={deleteMutation.isPending} onClick={closeConfirm} />
							</ButtonRow>
						</ModalBody>
					</Modal>
				</Portal>
			)}
		</Section>
	);
}

function qualityCheckErrorMessage(status: number): string {
	switch (status) {
		case 403:
			return "Sikringsordren kan ikke kvalitetssikres av den som opprettet den.";
		case 404:
			return "Fant ikke sikringsordren. Den kan være slettet.";
		default:
			return "Klarte ikke å oppdatere kvalitetssikringen. Prøv igjen senere.";
	}
}

/**
 * Hvorfor sikringsordren ikke kan kvalitetssikres av innlogget bruker, eller null når den kan det. Bare fullførte
 * sikringsordrer gir mening å kvalitetssikre, og backend avviser den som opprettet ordren.
 */
function qualityCheckBlocker(status: Sikringsordrestatus, createdByCurrentUser: boolean, canEdit: boolean): string | null {
	if (status !== Sikringsordrestatus.Completed) return `Bare fullførte sikringsordrer kan kvalitetssikres. Denne har status «${STATUS_LABEL[status]}».`;
	if (createdByCurrentUser) return "Du registrerte sikringsordren, så den må kvalitetssikres av noen andre.";
	if (!canEdit) return "Du har ikke tilgang til å kvalitetssikre sikringsordrer.";
	return null;
}

function useCurrentUserName(): string | null {
	const profile = useAuth().user?.profile;
	const name = [profile?.given_name, profile?.family_name].filter(Boolean).join(" ");
	return name || null;
}

interface QualityCheckSectionProps {
	orderId: string;
	status: Sikringsordrestatus;
	opprettetAv: string | null;
	kvalitetssikring: Kvalitetssikring | null;
	canEdit: boolean;
	sikring: Sikring;
	volumeKwh: number | null;
}

function QualityCheckSection({ orderId, status, opprettetAv, kvalitetssikring, canEdit, sikring, volumeKwh }: Readonly<QualityCheckSectionProps>) {
	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	const [checklistOpen, setChecklistOpen] = useState(false);
	const mutation = useStrombestillingApiMutation(`/sikringsordre/${orderId}/kvalitetssikret`, ["sikringsordre"], "PUT", true);
	// Backend avviser kvalitetssikring fra den som opprettet ordren. Sjekkes her også, så knappen ikke tilbys forgjeves.
	const currentUserName = useCurrentUserName();
	const createdByCurrentUser = !!opprettetAv && opprettetAv === currentUserName;
	const blocker = qualityCheckBlocker(status, createdByCurrentUser, canEdit);

	async function setQualityChecked(kvalitetssikret: boolean) {
		setErrorMessage(null);
		try {
			const response = await mutation.mutateAsync({ kvalitetssikret });
			if (!response.ok) {
				setErrorMessage(qualityCheckErrorMessage(response.status));
				return;
			}
			setChecklistOpen(false);
		} catch {
			setErrorMessage(qualityCheckErrorMessage(0));
		}
	}

	function closeChecklist() {
		if (mutation.isPending) return;
		setChecklistOpen(false);
		setErrorMessage(null);
	}

	return (
		<Section>
			<SectionTitle>Kvalitetssikring</SectionTitle>
			{kvalitetssikring ? (
				<QualityCheckedText>
					<FontAwesomeIcon icon={faCircleCheck} color={colors.green} />
					Kvalitetssikret av {kvalitetssikring.navn}, {fmtDateTime(kvalitetssikring.tidspunkt)}
				</QualityCheckedText>
			) : (
				<EmptyText>{blocker ?? "Sikringsordren er ikke kvalitetssikret ennå."}</EmptyText>
			)}
			{errorMessage && !checklistOpen && <ErrorText>{errorMessage}</ErrorText>}
			{!blocker && (
				<ButtonRow>
					{kvalitetssikring ? (
						<Button
							type="button"
							secondary
							label="Fjern kvalitetssikring"
							loading={mutation.isPending}
							disabled={mutation.isPending}
							onClick={() => setQualityChecked(false)}
						/>
					) : (
						<Button type="button" label="Kvalitetssikre" onClick={() => setChecklistOpen(true)} />
					)}
				</ButtonRow>
			)}
			{checklistOpen && (
				<QualityChecklistModal
					sikring={sikring}
					volumeKwh={volumeKwh}
					pending={mutation.isPending}
					errorMessage={errorMessage}
					onConfirm={() => setQualityChecked(true)}
					onClose={closeChecklist}
				/>
			)}
		</Section>
	);
}

interface QualityChecklistModalProps {
	sikring: Sikring;
	volumeKwh: number | null;
	pending: boolean;
	errorMessage: string | null;
	onConfirm: () => void;
	onClose: () => void;
}

/** Sjekkliste som må krysses av punkt for punkt før sikringsordren kan markeres som kvalitetssikret. */
function QualityChecklistModal({ sikring, volumeKwh, pending, errorMessage, onConfirm, onClose }: Readonly<QualityChecklistModalProps>) {
	const items: { name: string; value: string }[] = [
		{ name: "Periode", value: `${fmtDate(sikring.start)} – ${fmtDate(sikring.slutt)}` },
		{ name: "Volum", value: `${formatNumber(volumeKwh)} kWh` },
		{ name: "Kundepris (eks. mva)", value: fmtPriceWithUnit(priceOf(sikring)) },
		{ name: "Kjøpspris (eks. mva)", value: fmtPriceWithUnit(sikring.innpris) },
		{ name: "Margin (eks. mva)", value: fmtPriceWithUnit(sikring.fortjeneste) },
	];
	const [checked, setChecked] = useState<Set<string>>(new Set());
	const allChecked = items.every((item) => checked.has(item.name));

	function toggle(name: string, isChecked: boolean) {
		setChecked((prev) => {
			const next = new Set(prev);
			if (isChecked) next.add(name);
			else next.delete(name);
			return next;
		});
	}

	return (
		<Portal>
			<Modal onClose={onClose}>
				<ModalBody role="dialog" aria-modal="true" aria-labelledby="kvalitetssikring-tittel">
					<h2 id="kvalitetssikring-tittel" style={{ margin: "0 0 8px" }}>
						Kvalitetssikre sikringsordre
					</h2>
					<p style={{ margin: "0 0 16px" }}>Kontroller hvert punkt mot avtalen med kunden, og kryss av når det stemmer.</p>
					<ChecklistList>
						{items.map((item) => (
							<li key={item.name}>
								<ChecklistLabel>
									<input
										type="checkbox"
										checked={checked.has(item.name)}
										disabled={pending}
										onChange={(e) => toggle(item.name, e.target.checked)}
									/>
									<span>{item.name}</span>
									<ChecklistValue>{item.value}</ChecklistValue>
								</ChecklistLabel>
							</li>
						))}
					</ChecklistList>
					{errorMessage && <ErrorText>{errorMessage}</ErrorText>}
					<ButtonRow style={{ marginTop: "24px" }}>
						<Button type="button" label="Marker som kvalitetssikret" loading={pending} disabled={!allChecked || pending} onClick={onConfirm} />
						<Button type="button" secondary label="Avbryt" disabled={pending} onClick={onClose} />
					</ButtonRow>
				</ModalBody>
			</Modal>
		</Portal>
	);
}

function Field({ name, value }: Readonly<{ name: string; value: ReactNode }>) {
	return (
		<>
			<FieldName>{name}</FieldName>
			<div>{value}</div>
		</>
	);
}

// Farger med dekning er tokenet med alfa i hex: 59 = 35 %, 33 = 20 %.
const Backdrop = styled.div`
	position: fixed;
	inset: 0;
	background: ${colors.blue800}59;
	z-index: 40;
`;

const Panel = styled.div`
	position: fixed;
	top: 0;
	right: 0;
	bottom: 0;
	width: 520px;
	max-width: 100vw;
	background: ${colors.white};
	z-index: 41;
	box-shadow: -4px 0 16px ${colors.grey900}33;
	padding: 28px 30px;
	overflow-y: auto;
	box-sizing: border-box;
`;

const TopRow = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 4px;
`;

const CloseLink = styled.a`
	font-size: 1.25rem;
	color: ${colors.grey900};
`;

const StatusExplanation = styled.p`
	margin: 0 0 20px;
	font-size: 0.875rem;
	color: ${colors.grey700};
	line-height: 1.5;
`;

const FieldGrid = styled.div`
	display: grid;
	grid-template-columns: 150px 1fr;
	row-gap: 12px;
	column-gap: 14px;
	font-size: 0.875rem;
	line-height: 1.5;
`;

const FieldName = styled.div`
	font-weight: 700;
	font-size: 0.875rem;
	color: ${colors.grey800};
`;

const Section = styled.div`
	margin-top: 24px;
	padding-top: 18px;
	box-shadow: inset 0 1px 0 ${colors.grey300};
`;

const SectionTitle = styled.div`
	font-weight: 700;
	font-size: 0.875rem;
	color: ${colors.grey800};
	margin-bottom: 10px;
`;

const EmptyText = styled.p`
	margin: 0;
	font-size: 0.875rem;
	color: ${colors.grey700};
`;

const Guid = styled.span`
	font-family: monospace;
	font-size: 0.75rem;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

const TechnicalSummary = styled.summary`
	cursor: pointer;
	font-weight: 700;
	font-size: 0.875rem;
	color: ${colors.grey800};
`;

const TechnicalList = styled.div`
	display: flex;
	flex-direction: column;
	gap: 6px;
	margin-top: 10px;
`;

const TechnicalRow = styled.div`
	display: grid;
	grid-template-columns: 170px 1fr auto;
	gap: 10px;
	align-items: center;
	font-size: 0.875rem;
	color: ${colors.grey800};
`;

const FailureBox = styled.div`
	border-radius: 9px;
	box-shadow: inset 0 0 0 1px ${colors.coral800};
	padding: 14px 18px;
	margin-bottom: 20px;
	font-size: 0.875rem;
	line-height: 1.5;
	color: ${colors.coral};
`;

const ButtonRow = styled.div`
	display: flex;
	gap: 8px;
	margin-top: 12px;
`;

const ModalBody = styled.div`
	padding: 1.5rem;
	max-width: 28rem;
`;

const ErrorText = styled.p`
	margin: 10px 0 0;
	font-size: 0.875rem;
	color: ${colors.coral};
`;

const ChecklistList = styled.ul`
	list-style: none;
	margin: 0;
	padding: 0;
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
`;

const ChecklistLabel = styled.label`
	display: grid;
	grid-template-columns: auto 10rem 1fr;
	align-items: center;
	gap: 0.75rem;
	cursor: pointer;
	font-size: 0.875rem;
	font-weight: 700;
	color: ${colors.grey800};
`;

const ChecklistValue = styled.span`
	font-weight: 400;
`;

const QualityCheckedText = styled.p`
	display: flex;
	align-items: center;
	gap: 8px;
	margin: 0;
	font-size: 0.875rem;
	color: ${colors.grey800};
`;

const FailureTitle = styled.div`
	font-weight: 700;
	margin-bottom: 4px;
`;
